/* oxlint-disable no-inner-declarations */
import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';
import { map } from 'lodash';
import { z } from 'zod';

import type { BodyOf } from '@/api/fe-api';
import type { CurrentAssetContextValue } from '@/components/current-asset-provider';
import {
  AlertType,
  AlertTypeSchema,
  alertTypesToApi,
} from '@/entities/alert-type';
import { CustomerType } from '@/entities/customer-type';
import {
  codec,
  useQueryParams,
  type UseQueryParamsResult,
} from '@/hooks/router/use-query-params';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useMe } from '@/hooks/use-me';
import {
  AlertStatus,
  AlertStatusSchema,
} from '@/task-manager/entities/alert-status';
import type { AlertSuggestion } from '@/task-manager/entities/alert-suggestion';
import {
  TaskCardType,
  taskCardTypeSchema,
} from '@/task-manager/entities/task-card-type';
import type { TaskPreview } from '@/task-manager/entities/task-preview';
import {
  TaskPriority,
  taskPrioritySchema,
} from '@/task-manager/entities/task-priority';
import { ViewMode, viewModeSchema } from '@/task-manager/types/view-mode';
import { Optimistic } from '@/utils/react-query/optimistic';

import {
  type ListCardsViewOutput,
  tasksV1CardsListRetrieve,
} from '../fetch-client';
import { alertSuggestionMapper } from '../mappers/alert-suggestion';
import { taskPreviewMapper } from '../mappers/task-preview';
import { useFetchOpts } from '../use-fetch-opts';

export namespace ListTasks {
  export function key({ filters }: Input) {
    const key: unknown[] = ['task-manager', 'list-tasks'];

    if (filters) {
      key.push({
        alertStatusList: filters.alertStatusList,
        alertTypeList: filters.alertTypeList,
        priority: filters.priority,
        groupId: filters.groupId,
        topId: filters.topId,
        assigneeEmail: filters.assigneeEmail,
        cardType: filters.cardType,
        workstreamId: filters.workstreamId,
        viewMode: filters.viewMode,
      });
    }

    return key;
  }

  export type QueryParams = {
    refetchInterval?: number;
    keepPreviousData?: boolean;
  };

  export type Input = {
    filters?: Filters;
    includePcsAlerts?: boolean;
    includeAlerts?: boolean;
  };

  export type Filters = {
    alertStatusList?: AlertStatus[];
    alertTypeList?: AlertType[];
    priority?: TaskPriority;
    groupId?: string;
    topId?: string;
    assigneeEmail?: string;
    cardType?: TaskCardType;
    workstreamId?: string;
    viewMode?: ViewMode;
  };

  export type Output = {
    tasks: TaskPreview[];
    alertSuggestions: AlertSuggestion[];
  };

  function outputFromBody(data: ListCardsViewOutput): Output {
    return {
      tasks: map(data.existing_cards, taskPreviewMapper),
      alertSuggestions: map(data.suggestions, alertSuggestionMapper),
    };
  }

  function bodyFromInput(
    { filters }: Input,
    includePcsAlerts: boolean,
    includeWorkstreamsCards: boolean,
    customerType: CustomerType,
  ): BodyOf<typeof tasksV1CardsListRetrieve> {
    return {
      customerType,
      alertStatusList: filters?.alertStatusList?.filter((v) =>
        Object.values(AlertStatus).includes(v),
      ),
      alertTypesList: alertTypesToApi(filters?.alertTypeList),
      assigneeEmail: filters?.assigneeEmail,
      priority: filters?.priority,
      frontendId: filters?.groupId ?? filters?.topId,
      includePcsAlerts,
      cardType: filters?.cardType,
      workstreamUuid: filters?.workstreamId,
      includeWorkstreamsCards,
    };
  }

  export const optimistic = Optimistic.makeUpdate<Output>();

  export const useGetFiltersFromQueryParams = (
    currentAsset?: CurrentAssetContextValue,
  ): UseQueryParamsResult<Filters> => {
    const fields: Record<string, any> = {
      alertStatusList: {
        key: 'alert_status',
        schema: z.array(AlertStatusSchema).optional(),
        codec: codec.array(codec.string),
      },
      alertTypeList: {
        key: 'alert_type',
        schema: z.array(AlertTypeSchema).optional(),
        codec: codec.array(codec.string),
      },
      assigneeEmail: {
        key: 'assignee',
        schema: z.string().email().optional(),
        codec: codec.string,
      },
      priority: {
        key: 'priority',
        schema: taskPrioritySchema.optional(),
        codec: codec.string,
      },
      groupId: {
        key: 'group',
        schema: z.string().optional(),
        codec: codec.string,
      },
      cardType: {
        key: 'card_type',
        schema: taskCardTypeSchema.optional(),
        codec: codec.string,
      },
      workstreamId: {
        key: 'workstream',
        schema: z.string().optional(),
        codec: codec.string,
      },
      viewMode: {
        key: 'view_mode',
        schema: viewModeSchema.optional(),
        codec: codec.string,
      },
    };
    if (!currentAsset?.assetId) {
      fields.topId = {
        key: 'top',
        schema: z.string().optional(),
        codec: codec.string,
      };
    }
    return useQueryParams(fields);
  };

  export function useQuery(
    { includeAlerts = true, ...input }: Input,
    { refetchInterval, keepPreviousData }: QueryParams = {},
  ) {
    const getOpts = useFetchOpts();
    const featureFlag = useFeatureFlag();
    const includePcsAlerts = featureFlag('pcs');
    const includeWorkstreams =
      input.filters?.viewMode === ViewMode.PROJECT_MANAGEMENT &&
      featureFlag('projectManagement');
    const cardType = includeWorkstreams
      ? TaskCardType.STANDARD
      : input.filters?.cardType;
    const me = useMe();

    return rq.useQuery<Output>({
      refetchInterval,
      keepPreviousData,
      queryKey: key(input),
      async queryFn({ signal }) {
        const opts = await getOpts();
        const body = bodyFromInput(
          { ...input, filters: { ...input.filters, cardType } },
          includePcsAlerts,
          includeWorkstreams,
          me.config.type,
        );
        let data = await ok(
          tasksV1CardsListRetrieve(body, {
            ...opts,
            signal,
          }),
        );

        if (!includeAlerts) {
          data = {
            existing_cards: data.existing_cards.filter(
              (card) => card.card_type !== TaskCardType.ALERT,
            ),
            suggestions: [],
          };
        }
        return outputFromBody(data);
      },
    });
  }
}
